import { useState, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import RequestTracker from './components/RequestTracker';

const HISTORY_KEY = 'support_request_history';

export default function Support() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [requestCode, setRequestCode] = useState('');
  const [quickCode, setQuickCode] = useState('');
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        localStorage.removeItem(HISTORY_KEY);
        setHistory([]);
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const addToHistory = (code: string) => {
    setHistory((prev) => {
      const next = [code, ...prev.filter((c) => c !== code)].slice(0, 10);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return next;
    });
  };

  const clearHistory = () => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('Đã gửi yêu cầu thành công!');
      form.reset();
      return;
    }
    const name = (formData.get('name') as string || '').trim();
    const subject = (formData.get('subject') as string || '').trim();
    const messageText = (formData.get('message') as string || '').trim();
    setSubmitting(true);
    setFormError('');
    setRequestCode('');
    try {
      const { data: code, error } = await supabase.rpc('create_support_request', {
        p_name: name,
        p_subject: subject,
        p_message: messageText,
      });
      if (error) throw error;
      setRequestCode(code as string);
      addToHistory(code as string);
      setStatus('success');
      setMessage('Cảm ơn bạn! Chúng tôi đã nhận được yêu cầu và sẽ phản hồi sớm.');
      form.reset();
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error && err.message ? err.message : 'Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  const quickLookup = (code?: string) => {
    const target = code || requestCode;
    setQuickCode(target);
    addToHistory(target);
    setTimeout(() => {
      document.getElementById('tra-cuu-yeu-cau')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-4xl px-4 md:px-6 py-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Trung tâm hỗ trợ</h1>
            <p className="mt-2 text-foreground-600">Chúng tôi luôn sẵn sàng giúp đỡ bạn.</p>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-4 md:px-6 py-10">
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Gửi yêu cầu hỗ trợ</h2>
            <p className="text-sm text-foreground-600 mb-6">Điền thông tin bên dưới, chúng tôi sẽ phản hồi trong thời gian sớm nhất.</p>

            {status === 'success' && (
              <div className="mb-5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-emerald-400 text-xl"></i>
                  <p className="text-sm text-emerald-400">{message}</p>
                </div>
                {requestCode && (
                  <div className="mt-3 rounded-md bg-background-50 border border-emerald-500/20 p-3">
                    <p className="text-xs text-foreground-600 mb-2">Mã yêu cầu của bạn (lưu lại để tra cứu trạng thái):</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-lg font-bold tracking-widest text-emerald-400">{requestCode}</span>
                      <button onClick={() => quickLookup()} className="px-3 py-1.5 rounded-md bg-emerald-500/20 text-emerald-300 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-emerald-500/30 transition-colors">
                        Tra cứu nhanh
                      </button>
                      <Link to="/kiem-tra-yeu-cau" className="px-3 py-1.5 rounded-md bg-emerald-500/20 text-emerald-300 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-emerald-500/30 transition-colors">
                        Kiểm tra trạng thái
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
            {status === 'error' && (
              <div className="mb-5 rounded-lg bg-rose-500/15 border border-rose-500/30 p-4 flex items-center gap-3">
                <i className="ri-error-warning-line text-rose-400 text-xl"></i>
                <p className="text-sm text-rose-400">{formError}</p>
              </div>
            )}

            <form id="support-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                placeholder="Website"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tên đăng nhập</label>
                  <input name="name" required placeholder="Nhập tên đăng nhập" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Chủ đề</label>
                  <select name="subject" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500">
                    <option>Nạp / rút tiền</option>
                    <option>Giao dịch vàng</option>
                    <option>Xác minh danh tính</option>
                    <option>Vấn đề kỹ thuật</option>
                    <option>Khác</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nội dung</label>
                <textarea name="message" required maxLength={500} rows={4} placeholder="Mô tả chi tiết vấn đề của bạn (tối đa 500 ký tự)" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none" />
              </div>
              <button type="submit" disabled={submitting} className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors">
                {submitting ? 'Đang gửi...' : 'Gửi yêu cầu'}
              </button>
            </form>
          </div>

          <div id="tra-cuu-yeu-cau" className="rounded-xl border border-background-200/60 bg-background-100 p-6 md:p-8">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <h2 className="text-xl font-bold text-foreground-950">Tra cứu trạng thái yêu cầu</h2>
              {history.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-xs font-semibold text-rose-400 hover:text-rose-300 whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-delete-bin-line mr-1"></i> Xóa lịch sử
                </button>
              )}
            </div>
            <p className="text-sm text-foreground-600 mb-6">Nhập mã yêu cầu bạn đã nhận để xem trạng thái và câu trả lời của chúng tôi.</p>

            {history.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-semibold text-foreground-600 mb-2">Yêu cầu đã tra cứu / gửi:</p>
                <div className="flex flex-wrap gap-2">
                  {history.map((hc) => (
                    <button
                      key={hc}
                      onClick={() => quickLookup(hc)}
                      className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
                    >
                      {hc}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <RequestTracker prefillCode={quickCode} onSearchSuccess={addToHistory} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}